import {
  ArrowTopRightOnSquareIcon,
  CubeTransparentIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";

import usePartByEbayItem from "../../data/usePartByEbayItem";
import classNames from "../../utils/classNames";

interface ConversationPartChipProps {
  /** eBay item id (aka listing id) the message is written against. */
  itemID: string | undefined;
  /** Falls back to the message's own itemTitle while the part resolves. */
  fallbackTitle?: string | null;
  /** Compact variant used inline on the conversation list rows. */
  size?: "xs" | "sm";
}

/**
 * Tiny, self-fetching chip that resolves an eBay item id to the matching
 * stock part so we can surface the part thumbnail + a direct link to its
 * stock detail page right next to the message preview. Designed to be
 * cheap to render in lists — the underlying hook dedupes by itemID so
 * repeated items on the same page only trigger one request.
 */
export default function ConversationPartChip({
  itemID,
  fallbackTitle,
  size = "sm",
}: ConversationPartChipProps) {
  const { part, isLoading } = usePartByEbayItem(itemID);
  const thumb = part?.cdnThumbnail ?? part?.ebayThumbnail ?? null;
  const partTitle = part?.title ?? fallbackTitle ?? null;
  const stockHref = part?.id ? `/stock/${part.id}` : null;

  const dims =
    size === "xs"
      ? "h-8 w-8"
      : "h-10 w-10";

  return (
    <div
      className={classNames(
        "mt-1 inline-flex max-w-full items-center gap-2 rounded-md bg-slate-50 p-1 pr-2 ring-1 ring-inset ring-slate-200",
        size === "xs" ? "text-[11px]" : "text-xs"
      )}
    >
      <div
        className={classNames(
          "relative flex-none overflow-hidden rounded bg-white ring-1 ring-slate-200",
          dims
        )}
      >
        {thumb ? (
          <img
            src={thumb}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-slate-400">
            {isLoading ? (
              <span
                className="h-3 w-3 animate-pulse rounded-full bg-slate-300"
                aria-hidden
              />
            ) : (
              <PhotoIcon className="h-4 w-4" aria-hidden />
            )}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        {partTitle ? (
          <p className="truncate font-medium text-slate-700">{partTitle}</p>
        ) : (
          <p className="truncate text-slate-400">
            {isLoading ? "Looking up part…" : "No linked stock part"}
          </p>
        )}
        <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-slate-500">
          {part?.tagNumber && (
            <span className="inline-flex items-center gap-0.5">
              <CubeTransparentIcon className="h-3 w-3" aria-hidden />
              <span className="font-mono">{part.tagNumber}</span>
            </span>
          )}
          {stockHref && (
            <a
              href={stockHref}
              onClick={(e) => e.stopPropagation()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 font-semibold text-primary-700 hover:text-primary-800 hover:underline"
            >
              View stock
              <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
